import type { ComposerCapabilityCategory, ComposerCapabilityExample } from '@/types/composer';
import menuGroups from '@/lib/composer-menu-groups.json';

type MenuGroupEntry = { title?: string; icon?: string; order?: number };

const MENU_GROUP_REGISTRY = menuGroups as Record<string, MenuGroupEntry>;

type ClientWithComposerMenu = {
  composerMenu?: { categories?: ComposerCapabilityCategory[] };
};

const TERMINAL_ELLIPSIS_RE = /\s*(?:…|\.{3})\s*$/;

function stripTerminalEllipsis(text: string): string {
  return text.replace(TERMINAL_ELLIPSIS_RE, '').trim();
}

function ensureSentence(text: string): string {
  const clean = stripTerminalEllipsis(text.replace(/\s+/g, ' ').trim());
  if (!clean) return '';
  return /[.!?]$/.test(clean) ? clean : `${clean}.`;
}

function sanitizeExample(example: ComposerCapabilityExample): ComposerCapabilityExample {
  const message = ensureSentence(example.message || example.label || '');
  const placeholder = ensureSentence(example.placeholder || message);
  return {
    label: stripTerminalEllipsis(example.label || message),
    message,
    placeholder,
  };
}

/** Merge catalog menuGroup title/icon onto tenant-configured category shells. */
export function enrichComposerMenuCategory(
  category: ComposerCapabilityCategory,
): ComposerCapabilityCategory {
  const registry = MENU_GROUP_REGISTRY[category.id];
  return {
    ...category,
    title: category.title || registry?.title || category.id,
    icon: category.icon || registry?.icon || 'Bot',
    examples: (category.examples || []).map(sanitizeExample),
  };
}

export function enrichComposerMenuCategories(
  categories: ComposerCapabilityCategory[],
): ComposerCapabilityCategory[] {
  return categories.map(enrichComposerMenuCategory);
}

export function resolveStaticComposerMenuCategories(
  client: ClientWithComposerMenu,
): ComposerCapabilityCategory[] {
  return enrichComposerMenuCategories(client.composerMenu?.categories ?? []);
}

export function filterComposerMenuCategories(
  categories: ComposerCapabilityCategory[],
  focusKey: string | null | undefined
): ComposerCapabilityCategory[] {
  const focus = focusKey?.trim().toLowerCase() ?? '';
  return categories.filter((cat) => {
    const focusFilter = cat.focus;
    if (!focusFilter?.length) return true;
    if (!focus) return focusFilter.includes('default');
    return focusFilter.map((f) => f.toLowerCase()).includes(focus);
  });
}
